import { Link } from 'react-router-dom';
import { Phone, Mail, Instagram, Facebook, Globe, MapPin, ShieldCheck } from 'lucide-react';
import { business, logoUrl } from '../../mock';
import { suburbs } from '../../data/suburbs';

// Compact one-line certification strip. Real credentials — surfaces trust for
// procurement / property managers without eating a whole footer band.
const CERTIFICATIONS = [
  'ABN 24 552 872 598',
  'Public Liability Insured (Policy ****PZBI)',
  'NDIS Registered Provider',
  'DVA Approved Provider',
  "Working With Children's Check",
  'Police Check Cleared',
];

export default function Footer() {
  return (
    <footer className="dark-green-bg pt-14 pb-6">
      <div className="max-w-7xl mx-auto px-4 lg:px-8">
        {/* Top: brand + link columns */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-8">
          {/* Brand + contact */}
          <div className="lg:col-span-5">
            <div className="flex items-center gap-3 mb-4">
              <div className="bg-white inline-block p-2.5 rounded-lg">
                <img src={logoUrl} alt="Vic Garden and Lawn Maintenance logo" className="h-9 w-auto" />
              </div>
              <div className="leading-tight" data-testid="footer-business-name">
                <div className="text-white font-extrabold text-base" style={{ fontFamily: 'Inter, sans-serif' }}>
                  Vic Garden and Lawn Maintenance
                </div>
                <div className="text-[10px] font-bold tracking-[0.18em] text-[#D98A00] mt-0.5">
                  RELIABLE · LOCAL · TRUSTED
                </div>
              </div>
            </div>
            <p className="text-white/70 text-sm mb-4 leading-relaxed max-w-sm">
              Reliable lawn &amp; garden maintenance for homes across Ballarat. Plus commercial grounds care across Victoria.
            </p>
            <div className="space-y-1.5 text-sm">
              <a href={`tel:${business.phone}`} className="flex items-center gap-2 text-white hover:text-[#D98A00]">
                <Phone className="w-4 h-4" /> <span className="font-bold">{business.phone}</span>
              </a>
              <a href={`mailto:${business.email}`} className="flex items-center gap-2 text-white/80 hover:text-[#D98A00]">
                <Mail className="w-4 h-4" /> {business.email}
              </a>
              <div className="flex items-start gap-2 text-white/80">
                <MapPin className="w-4 h-4 mt-0.5" /> <span>{business.address}</span>
              </div>
            </div>
            <div className="flex items-center gap-2.5 mt-4">
              {[
                { icon: Instagram, href: business.socials.instagram, label: 'Instagram' },
                { icon: Facebook, href: business.socials.facebook, label: 'Facebook' },
                { icon: Globe, href: business.socials.google, label: 'Google' },
              ].map((s) => {
                const Icon = s.icon;
                return (
                  <a
                    key={s.label}
                    href={s.href}
                    target="_blank"
                    rel="noreferrer"
                    className="w-8 h-8 rounded-full bg-white/10 hover:bg-[#D98A00] hover:text-[#0A2E18] flex items-center justify-center transition-colors"
                    aria-label={s.label}
                  >
                    <Icon className="w-3.5 h-3.5" />
                  </a>
                );
              })}
            </div>
          </div>

          {/* Business */}
          <div className="lg:col-span-2">
            <h4 className="text-[#D98A00] font-bold uppercase tracking-wider text-xs mb-3">Business</h4>
            <ul className="space-y-1.5 text-sm text-white/80">
              <li><Link to="/" onClick={() => window.scrollTo(0, 0)} className="hover:text-white">Home</Link></li>
              <li><Link to="/commercial" onClick={() => window.scrollTo(0, 0)} className="hover:text-white">Commercial</Link></li>
              <li><a href="/#gallery" className="hover:text-white">Projects</a></li>
              <li><Link to="/blog" onClick={() => window.scrollTo(0, 0)} className="hover:text-white">Insights &amp; Advice</Link></li>
              <li><a href="/#faq" className="hover:text-white">FAQ</a></li>
              <li><a href="/#contact" className="hover:text-white">Contact</a></li>
              <li><Link to="/portal" onClick={() => window.scrollTo(0, 0)} className="hover:text-white" data-testid="footer-portal-link">Customer Login</Link></li>
            </ul>
          </div>

          {/* Services */}
          <div className="lg:col-span-2">
            <h4 className="text-[#D98A00] font-bold uppercase tracking-wider text-xs mb-3">Services</h4>
            <ul className="space-y-1.5 text-sm text-white/80">
              <li><a href="/#services" className="hover:text-white">Lawn Mowing</a></li>
              <li><a href="/#services" className="hover:text-white">Gardening</a></li>
              <li><a href="/#services" className="hover:text-white">Weed Control</a></li>
              <li><a href="/#services" className="hover:text-white">Acreage Mowing</a></li>
            </ul>
          </div>

          {/* Areas */}
          <div className="lg:col-span-3">
            <h4 className="text-[#D98A00] font-bold uppercase tracking-wider text-xs mb-3">Service Areas</h4>
            <ul className="space-y-1.5 text-sm text-white/80">
              {suburbs.slice(0, 5).map((s) => (
                <li key={s.slug}>
                  <Link to={`/service-areas/${s.slug}`} onClick={() => window.scrollTo(0, 0)} className="hover:text-white">
                    {s.name}
                  </Link>
                </li>
              ))}
              <li>
                <Link to="/service-areas" onClick={() => window.scrollTo(0, 0)} className="text-[#D98A00] hover:text-white font-semibold">
                  View all &rarr;
                </Link>
              </li>
            </ul>
          </div>
        </div>

        {/* Hours — horizontal strip across the full footer width */}
        <div className="border-t border-white/10 mt-10 pt-5" data-testid="footer-hours">
          <div className="flex flex-col md:flex-row md:items-center md:justify-center gap-2 md:gap-6 flex-wrap text-sm">
            <span className="text-[#D98A00] font-bold uppercase tracking-wider text-xs md:mr-2">Hours</span>
            {business.hours.map(([day, time]) => (
              <span key={day} className="flex items-center gap-2 text-white/80">
                <span className="font-semibold text-white">{day}</span>
                <span className="text-white/60">{time}</span>
              </span>
            ))}
          </div>
        </div>

        {/* Inline certifications strip */}
        <div className="border-t border-white/10 mt-5 pt-5 flex flex-col md:flex-row items-start md:items-center md:justify-between gap-3">
          <div className="flex items-center gap-2 flex-wrap text-[11px] text-white/60" data-testid="footer-certifications">
            <ShieldCheck className="w-3.5 h-3.5 text-[#D98A00]" aria-hidden="true" />
            {CERTIFICATIONS.map((c, i) => (
              <span key={c} className="flex items-center gap-2">
                <span className="whitespace-nowrap">{c}</span>
                {i < CERTIFICATIONS.length - 1 && <span className="text-white/25">·</span>}
              </span>
            ))}
          </div>
        </div>

        <div className="mt-4 pt-4 border-t border-white/10 flex flex-col md:flex-row items-center justify-between gap-2 text-xs text-white/50">
          <p>&copy; {new Date().getFullYear()} Vic Garden and Lawn Maintenance. All rights reserved.</p>
          <div className="flex items-center gap-4">
            <Link to="/terms" onClick={() => window.scrollTo(0, 0)} className="hover:text-white">Terms &amp; Conditions</Link>
            <Link to="/privacy" onClick={() => window.scrollTo(0, 0)} className="hover:text-white">Privacy Policy</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
